import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useWorkspaceStore, selectActiveWorkspace, selectCapabilitiesList } from '@/stores/workspaceStore';
import { useUIStore } from '@/stores/uiStore';
import { useCapabilities } from '@/hooks/useCapabilities';
import AppShell from '@/components/layout/AppShell';
import { CapabilityType } from '@/types';
import type { Capability } from '@/types';

// ─── Type / status colors ─────────────────────────────────────────────────────

const TYPE_LABEL: Record<string, string> = {
  skill: 'Skill',
  knowledge: 'Knowledge',
  experience: 'Experience',
  tool: 'Tool',
  relationship: 'Relationship',
  credential: 'Credential',
  domain: 'Domain',
};

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  growing: 'Growing',
  dormant: 'Dormant',
  deprecated: 'Deprecated',
};

const STATUS_DOT: Record<string, string> = {
  active: '#22c55e',
  growing: '#3b82f6',
  dormant: '#eab308',
  deprecated: '#ef4444',
};

// ─── Card ─────────────────────────────────────────────────────────────────────

const CapabilityRow: React.FC<{ capability: Capability; onClick: () => void }> = ({
  capability,
  onClick,
}) => {
  const snippet = capability.summary?.slice(0, 140) ?? '';
  const strengthPct = Math.max(0, Math.min(100, capability.strength));
  const statusColor = STATUS_DOT[capability.status] ?? 'var(--text-muted)';

  return (
    <button
      type="button"
      onClick={onClick}
      className="card text-left w-full p-4 flex flex-col gap-3 group relative overflow-hidden transition-all"
      style={{ cursor: 'pointer' }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span
              className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded"
              style={{
                fontFamily: 'var(--font-display)',
                letterSpacing: '0.08em',
                background: 'var(--bg-high)',
                color: 'var(--text-dim)',
                border: '1px solid var(--border)',
              }}
            >
              {TYPE_LABEL[capability.type] ?? capability.type}
            </span>
            <span
              className="inline-flex items-center gap-1 text-[10px] uppercase font-bold"
              style={{
                fontFamily: 'var(--font-display)',
                letterSpacing: '0.1em',
                color: 'var(--text-muted)',
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: statusColor }}
              />
              {STATUS_LABEL[capability.status] ?? capability.status}
            </span>
          </div>

          <h3
            className="text-base font-bold leading-tight mb-1 truncate"
            style={{
              fontFamily: 'var(--font-display)',
              color: 'var(--text)',
              letterSpacing: '0.01em',
              textTransform: 'uppercase',
            }}
          >
            {capability.title}
          </h3>

          {snippet && (
            <p
              className="text-xs leading-relaxed"
              style={{
                color: 'var(--text-muted)',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {snippet}
            </p>
          )}
        </div>

        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <div
            className="text-[10px] uppercase font-bold"
            style={{
              fontFamily: 'var(--font-display)',
              color: 'var(--crimson-light)',
              letterSpacing: '0.1em',
            }}
          >
            {strengthPct}%
          </div>
          <div
            className="w-12 h-1 rounded-full overflow-hidden"
            style={{ background: 'var(--bg-high)' }}
          >
            <div
              className="h-full rounded-full"
              style={{ width: `${strengthPct}%`, background: 'var(--crimson)' }}
            />
          </div>
        </div>
      </div>

      {capability.tags.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {capability.tags.slice(0, 5).map((t) => (
            <span
              key={t}
              className="text-[10px] px-1.5 py-0.5 rounded"
              style={{
                background: 'var(--bg-high)',
                color: 'var(--text-dim)',
                border: '1px solid var(--border)',
              }}
            >
              #{t}
            </span>
          ))}
          {capability.tags.length > 5 && (
            <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
              +{capability.tags.length - 5}
            </span>
          )}
        </div>
      )}
    </button>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const TYPE_FILTERS: { value: string; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: CapabilityType.SKILL, label: 'Skills' },
  { value: CapabilityType.KNOWLEDGE, label: 'Knowledge' },
  { value: CapabilityType.EXPERIENCE, label: 'Experience' },
  { value: CapabilityType.TOOL, label: 'Tools' },
  { value: CapabilityType.RELATIONSHIP, label: 'Relationships' },
];

const CapabilityListPage: React.FC = () => {
  const history = useHistory();
  const activeWorkspace = useWorkspaceStore(selectActiveWorkspace);
  const workspaceId = activeWorkspace?.id ?? null;
  const capabilities = useWorkspaceStore(selectCapabilitiesList);
  const openQuickCapture = useUIStore((s) => s.openQuickCapture);

  const { isLoading } = useCapabilities(workspaceId, { pageSize: 200 });

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return capabilities.filter((c) => {
      if (typeFilter !== 'all' && c.type !== typeFilter) return false;
      if (!q) return true;
      return (
        c.title.toLowerCase().includes(q) ||
        (c.summary?.toLowerCase().includes(q) ?? false) ||
        c.tags.some((t) => t.toLowerCase().includes(q))
      );
    });
  }, [capabilities, search, typeFilter]);

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 pb-12 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="eyebrow mb-2">Capabilities</p>
            <h1
              className="text-2xl font-bold leading-tight"
              style={{
                fontFamily: 'var(--font-display)',
                letterSpacing: '0.02em',
                color: 'var(--text)',
                textTransform: 'uppercase',
              }}
            >
              Your Capability Layer
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
              {capabilities.length > 0
                ? `${capabilities.length} ${capabilities.length === 1 ? 'capability' : 'capabilities'} in this workspace`
                : 'Build your compound layer of skills, knowledge, and leverage'}
            </p>
          </div>

          <button
            type="button"
            onClick={() => openQuickCapture()}
            className="btn-primary flex-shrink-0"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add Capability
          </button>
        </div>

        {/* Search + filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
              style={{ color: 'var(--text-muted)' }}
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search capabilities..."
              className="w-full pl-9 pr-3 py-2 text-sm rounded outline-none"
              style={{
                background: 'var(--bg-mid)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
              }}
            />
          </div>

          <div className="flex items-center gap-1.5 flex-wrap">
            {TYPE_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setTypeFilter(f.value)}
                className="text-[10px] uppercase font-bold px-2.5 py-1.5 rounded transition-colors"
                style={{
                  fontFamily: 'var(--font-display)',
                  letterSpacing: '0.1em',
                  background:
                    typeFilter === f.value ? 'var(--crimson)' : 'var(--bg-mid)',
                  color:
                    typeFilter === f.value ? '#fff' : 'var(--text-dim)',
                  border: `1px solid ${
                    typeFilter === f.value ? 'var(--crimson)' : 'var(--border)'
                  }`,
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        {isLoading && capabilities.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="card p-4 animate-pulse"
                style={{ background: 'var(--bg-mid)', minHeight: '120px' }}
              />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="card p-10 text-center">
            <p className="eyebrow mb-3 justify-center">
              {capabilities.length === 0 ? 'Empty' : 'No matches'}
            </p>
            <h2
              className="text-xl font-bold mb-3"
              style={{
                fontFamily: 'var(--font-display)',
                color: 'var(--text)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              {capabilities.length === 0
                ? 'No capabilities yet'
                : 'Nothing matches that filter'}
            </h2>
            <p
              className="text-sm mx-auto mb-5 max-w-sm"
              style={{ color: 'var(--text-muted)' }}
            >
              {capabilities.length === 0
                ? 'Capture your first skill, tool, or domain to start building your capability graph.'
                : 'Try a different search term or clear the filter.'}
            </p>
            {capabilities.length === 0 ? (
              <button
                type="button"
                onClick={() => openQuickCapture()}
                className="btn-primary mx-auto"
              >
                Capture your first capability
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setTypeFilter('all');
                }}
                className="btn-ghost mx-auto"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {filtered.map((capability) => (
              <CapabilityRow
                key={capability.id}
                capability={capability}
                onClick={() => history.push(`/capabilities/${capability.id}`)}
              />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
};

export default CapabilityListPage;
